/** Validates that a value is a non-empty string. */
function requiredString(name: string, value: string | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error(`Missing or empty required environment variable: ${name}`)
  }
  return value
}

/** Validates that a value is a valid URL string. */
function requiredUrl(name: string, value: string | undefined): string {
  const v = requiredString(name, value)
  try {
    new URL(v)
  } catch {
    throw new Error(`Environment variable ${name} is not a valid URL: "${v}"`)
  }
  return v
}

/** Validates that a value is one of the allowed values. */
function requiredEnum<T extends string>(
  name: string,
  value: string | undefined,
  allowed: readonly T[]
): T {
  const v = requiredString(name, value)
  if (!allowed.includes(v as T)) {
    throw new Error(
      `Environment variable ${name} must be one of [${allowed.join(", ")}], got: "${v}"`
    )
  }
  return v as T
}

interface Env {
  /* Client (NEXT_PUBLIC_*) */
  NEXT_PUBLIC_APP_FRONTEND_URL: string
  NEXT_PUBLIC_TAGCO_ANALYTICS_URL: string
  NEXT_PUBLIC_SEARCH_ENDPOINT: string
  NEXT_PUBLIC_SEARCH_INDEX: string
  NEXT_PUBLIC_SEARCH_ADMIN_KEY: string

  /* Server-only */
  KONTENT_ENV_ID: string
  KONTENT_PREVIEW_ENV_ID: string
  KONTENT_ENV_API_KEY: string
  KONTENT_PREVIEW_API_KEY: string
  KONTENT_APP_CODENAME: string
  EXCHANGE_RATE_API_KEY: string

  /* Shared */
  NODE_ENV: "development" | "production"
}

function createValidatedEnv(): Env {
  const isServer = typeof window === "undefined"

  return {
    // Client
    NEXT_PUBLIC_APP_FRONTEND_URL: requiredUrl(
      "NEXT_PUBLIC_APP_FRONTEND_URL",
      process.env.NEXT_PUBLIC_APP_FRONTEND_URL
    ),
    NEXT_PUBLIC_TAGCO_ANALYTICS_URL: requiredUrl(
      "NEXT_PUBLIC_TAGCO_ANALYTICS_URL",
      process.env.NEXT_PUBLIC_TAGCO_ANALYTICS_URL
    ),
    NEXT_PUBLIC_SEARCH_ENDPOINT: requiredString(
      "NEXT_PUBLIC_SEARCH_ENDPOINT",
      process.env.NEXT_PUBLIC_SEARCH_ENDPOINT
    ),
    NEXT_PUBLIC_SEARCH_INDEX: requiredString(
      "NEXT_PUBLIC_SEARCH_INDEX",
      process.env.NEXT_PUBLIC_SEARCH_INDEX
    ),
    NEXT_PUBLIC_SEARCH_ADMIN_KEY: requiredString(
      "NEXT_PUBLIC_SEARCH_ADMIN_KEY",
      process.env.NEXT_PUBLIC_SEARCH_ADMIN_KEY
    ),

    // Server-only — skip validation on client
    KONTENT_ENV_ID: isServer
      ? requiredString("KONTENT_ENV_ID", process.env.KONTENT_ENV_ID)
      : ("" as string),
    KONTENT_PREVIEW_ENV_ID: isServer
      ? requiredString(
          "KONTENT_PREVIEW_ENV_ID",
          process.env.KONTENT_PREVIEW_ENV_ID
        )
      : ("" as string),
    KONTENT_ENV_API_KEY: isServer
      ? requiredString("KONTENT_ENV_API_KEY", process.env.KONTENT_ENV_API_KEY)
      : ("" as string),
    KONTENT_PREVIEW_API_KEY: isServer
      ? requiredString(
          "KONTENT_PREVIEW_API_KEY",
          process.env.KONTENT_PREVIEW_API_KEY
        )
      : ("" as string),
    KONTENT_APP_CODENAME: isServer
      ? requiredString("KONTENT_APP_CODENAME", process.env.KONTENT_APP_CODENAME)
      : ("" as string),
    EXCHANGE_RATE_API_KEY: isServer
      ? requiredString(
          "EXCHANGE_RATE_API_KEY",
          process.env.EXCHANGE_RATE_API_KEY
        )
      : ("" as string),

    // Shared
    NODE_ENV: requiredEnum("NODE_ENV", process.env.NODE_ENV, [
      "development",
      "production",
    ] as const),
  }
}

export const env: Env = createValidatedEnv()
